import React from 'react';
import {
	BaseDraggableWidget,
	NotepadWidget,
	SummaryWidget,
} from '../WidgetDashboard/Card';
import moment from 'moment';
import CalendarUpcoming from '../WidgetDashboard/widgets/CalendarUpcoming';
import SystemGetStarted from '../WidgetDashboard/widgets/SystemGetStarted';
import LabStatus from '../WidgetDashboard/widgets/LabStatus';
import TicketsAll from '../WidgetDashboard/widgets/TicketsAll';
import VendorSearch from '../WidgetDashboard/widgets/VendorSearch';
import BlankWidget from '../WidgetDashboard/widgets/BlankWidget';
import AssetStatusChart from '../WidgetDashboard/widgets/AssetStatusChart';
import AssetUpcomingTasks from '../WidgetDashboard/widgets/AssetUpcomingTasks';
import AssetActiveContracts from '../WidgetDashboard/widgets/AssetActiveContracts';
import AssetOverview from '../WidgetDashboard/widgets/AssetOverview';
import AssetQuickSetup from '../WidgetDashboard/widgets/AssetSetup';
import Notes from '../WidgetDashboard/widgets/Notes';
import AssetOpenTickets from '../WidgetDashboard/widgets/AssetOpenTickets';
import MyTasks from '../WidgetDashboard/widgets/MyTasks';
import LiveFeed from '../WidgetDashboard/widgets/LiveFeed';

export const FIELD_OPTIONS = [
	{ label: 'Text Field', value: 'text' },
	{ label: 'Text Area', value: 'textarea' },
	{ label: 'Numeric', value: 'numeric' },
	{ label: 'Boolean', value: 'boolean' },
	//{ label: 'Image', value: 'image' },
	{ label: 'Service Vendor', value: 'vendor' },
	{ label: 'Category', value: 'category' },
	{ label: 'Location', value: 'location' },
];

export const TAG_COLOR_OPTIONS = [
	{
		key: 'gray',
		value: 'bg-gray-100 text-gray-800 ring-gray-300',
		preview: 'bg-gray-300 ring-gray-300',
	},
	{
		key: 'blue',
		value: '!bg-blue-100 !text-blue-800 !ring-blue-300',
		preview: 'bg-blue-300 ring-blue-300',
	},
	{
		key: 'red',
		value: 'bg-red-100 text-red-800 ring-red-300 font-medium',
		preview: 'bg-red-300 ring-red-300',
	},
	{
		key: 'green',
		value: 'bg-green-100 text-green-800 ring-green-300',
		preview: 'bg-green-300 ring-green-300',
	},
	{
		key: 'yellow',
		value: 'bg-yellow-100 text-yellow-800 ring-yellow-300',
		preview: 'bg-yellow-300 ring-yellow-300',
	},
	{
		key: 'indigo',
		value: 'bg-indigo-100 text-indigo-800 ring-indigo-300',
		preview: 'bg-indigo-300 ring-indigo-300',
	},
	{
		key: 'purple',
		value: 'bg-purple-100 text-purple-800 ring-purple-300',
		preview: 'bg-purple-300 ring-purple-300',
	},
	{
		key: 'pink',
		value: 'bg-pink-100 text-pink-800 ring-pink-300',
		preview: 'bg-pink-300 ring-pink-300',
	},
];

export const STATUS_MAPPING = {
	operational: {
		color: 'text-green-600/80',
		glow: 'text-green-400 bg-green-400/10',
		label: 'Operational',
	},
	partialDisruption: {
		color: 'text-yellow-600/80',
		glow: 'text-yellow-400 bg-yellow-400/10',
		label: 'Partial Disruption',
	},
	completeDisruption: {
		color: 'text-orange-600/80',
		glow: 'text-orange-400 bg-orange-400/10',
		label: 'Complete Disruption',
	},
	disruption: {
		color: 'text-orange-600/80',
		glow: 'text-orange-400 bg-orange-400/10',
		label: 'Disruption',
	},
	partialOutage: {
		color: 'text-orange-600/80',
		glow: 'text-orange-400 bg-orange-400/10',
		label: 'Partial Outage',
	},
	completeOutage: {
		color: 'text-red-600/80',
		glow: 'text-rose-400 bg-rose-400/10',
		label: 'Complete Outage',
	},
	outage: {
		color: 'text-red-600/80',
		bg: 'text-rose-400 bg-rose-400/10',
		label: 'Outage',
	},
	locationDisruption: {
		color: 'text-orange-600/80',
		glow: 'text-orange-400 bg-orange-400/10',
		label: 'Disruption',
	},
	locationOutage: {
		color: 'text-red-600/80',
		glow: 'text-rose-400 bg-rose-400/10',
		label: 'Outage',
	},
};

const generateInstanceId = () => {
	return Math.random().toString(36).substr(2, 9);
};

export const AVAILABLE_WIDGETS = {
	dashboard: [
		{
			id: generateInstanceId(),
			type: 'lane',
			data: { title: 'Active' },
			children: [],
		},
		{
			id: generateInstanceId(),
			type: 'lane',
			data: { title: 'Available' },
			children: [
				{
					id: 'dashboard-notepad',
					type: 'item',
					data: {
						title: 'Notepad',
					},
					children: (
						<BaseDraggableWidget title="Notepad">
							<NotepadWidget />
						</BaseDraggableWidget>
					),
				},
				{
					id: 'dashboard-system-overview',
					type: 'item',
					data: {
						title: 'Status Chart',
					},
					children: <LabStatus title="Lab Status" />,
				},
				{
					id: 'dashboard-vendor-search',
					type: 'item',
					data: {
						title: 'Vendor Search',
					},
					children: <VendorSearch title="Vendor Search" />,
				},
				{
					id: 'dashboard-calendar-upcoming',
					type: 'item',
					data: {
						title: 'Upcoming',
					},
					children: <CalendarUpcoming title="Upcoming" />,
				},
				{
					id: 'system-get-started',
					type: 'item',
					data: {
						title: 'Get Started',
						size: 'full',
					},
					children: <SystemGetStarted />,
				},
				{
					id: 'dashboard-tickets-all',
					type: 'item',
					data: {
						title: 'All Tickets',
					},
					children: <TicketsAll />,
				},
				{
					id: 'dashboard-notes',
					type: 'item',
					children: <Notes />,
				},
				{
					id: 'dashboard-my-tasks',
					type: 'item',
					data: {
						title: 'My Tasks',
					},
					children: <MyTasks />,
				},
				/*{
					id: 'dashboard-live-feed',
					type: 'item',
					data: {
						title: 'Live Feed',
						size: 'half',
					},
					children: <LiveFeed />,
				},*/
			],
		},
	],
	assetDashboard: [
		{
			id: generateInstanceId(),
			type: 'lane',
			children: [],
		},
		{
			id: generateInstanceId(),
			type: 'lane',
			children: [
				{
					id: 'asset-status-chart',
					type: 'item',
					children: <AssetStatusChart />,
				},
				{
					id: 'asset-upcoming-tasks',
					type: 'item',
					children: <AssetUpcomingTasks />,
				},
				{
					id: 'asset-active-contracts',
					type: 'item',
					children: <AssetActiveContracts />,
				},
				{
					id: 'asset-quick-setup',
					type: 'item',
					children: <AssetQuickSetup />,
				},
				{
					id: 'asset-open-tickets',
					type: 'item',
					children: <AssetOpenTickets />,
				},
			],
		},
	],
};

export const getAvailableWidgets = (type, keys = []) => {
	if (!keys || !keys.length) {
		return [];
	}

	const widgetObjectByType = AVAILABLE_WIDGETS[type];

	const arrOfWigets = widgetObjectByType ? widgetObjectByType[1]?.children : [];

	const availableWidgets = arrOfWigets.filter((x) => keys.includes(x?.id));

	let temp = widgetObjectByType[1];
	temp.children = availableWidgets;

	return { all: [widgetObjectByType[0], temp] };
};
